/**
 * Node customizado para condições lógicas (estilo Typebot)
 * Suporta múltiplos IFs com várias condições cada + ELSE fixo
 * Cada IF tem seu próprio handle de saída
 * Inclui toolbar flutuante ao selecionar
 */

import { Handle, Position, NodeProps } from '@xyflow/react';
import { GitBranch, Settings2 } from 'lucide-react';
import { FlowNodeData } from '@/types/flows';
import { useMemo } from 'react';
import { FlowNodeToolbar } from './FlowNodeToolbar';

/**
 * Mapeia operadores para labels amigáveis em português
 */
const operatorLabels: Record<string, string> = {
  equals: '=',
  not_equals: '≠',
  contains: 'contém',
  not_contains: 'não contém',
  starts_with: 'começa com',
  ends_with: 'termina com',
  greater: '>',
  less: '<',
  greater_equal: '≥',
  less_equal: '≤',
  is_set: 'está definido',
  is_empty: 'está vazio',
  has_items: 'tem itens',
  is_empty_array: 'array vazio',
  regex: 'regex',
  // Formatos legado
  '==': '=',
  '!=': '≠',
  '>': '>',
  '<': '<',
  '>=': '≥',
  '<=': '≤',
};

/**
 * Componente visual do node de condição (estilo Typebot)
 */
export function ConditionNode({ data, id, selected }: NodeProps) {
  const nodeData = data as FlowNodeData;
  const config = nodeData.config || {};
  
  // Callbacks injetados via FlowCanvas
  const onEdit = (nodeData as any).onEdit;
  const onDuplicate = (nodeData as any).onDuplicate;
  const onDelete = (nodeData as any).onDelete;
  const onTestFromHere = (nodeData as any).onTestFromHere;
  
  // Obtém comparisons (novo formato) ou converte formato legado
  const comparisons = useMemo(() => {
    // Novo formato com múltiplos IFs
    if (config.comparisons && Array.isArray(config.comparisons)) {
      return config.comparisons;
    }
    
    // Formato legado: converte para novo formato com um único IF
    if (config.conditions && Array.isArray(config.conditions)) {
      return [{
        id: 'legacy-if-1',
        conditions: config.conditions,
        logicOperator: config.logicOperator || 'and',
      }];
    }
    
    // Formato legado mais antigo (variable, operator, value diretos)
    if (config.variable) {
      return [{
        id: 'legacy-if-1',
        conditions: [{
          id: 'legacy-cond-1',
          variable: config.variable,
          operator: config.operator || 'equals',
          value: config.value || '',
        }],
        logicOperator: 'and',
      }];
    }
    
    // Nenhuma configuração
    return [];
  }, [config]);

  // Obtém informações das edges conectadas via data injetado
  const connectedHandles = useMemo(() => {
    const edges = (data as any).edges || [];
    return new Set(edges.map((e: any) => e.sourceHandle));
  }, [data]);

  /**
   * Formata resumo de um bloco IF
   */
  const formatIfSummary = (comparison: any) => {
    if (!comparison.conditions || comparison.conditions.length === 0) {
      return 'Configure...';
    }
    
    const first = comparison.conditions[0];
    const varName = first.variable || '?';
    const op = operatorLabels[first.operator] || first.operator;
    const val = first.value || '';
    
    // Para operadores sem valor
    if (first.operator === 'is_set' || first.operator === 'is_empty') {
      return `${varName} ${op}`;
    }
    
    let summary = `${varName} ${op} "${val.substring(0, 10)}${val.length > 10 ? '...' : ''}"`;
    
    // Se tem mais condições, mostra indicador
    if (comparison.conditions.length > 1) {
      const logicLabel = comparison.logicOperator === 'and' ? 'E' : 'OU';
      summary += ` ${logicLabel} +${comparison.conditions.length - 1}`;
    }
    
    return summary;
  };

  return (
    <div className={`bg-card rounded-lg shadow-lg border-2 min-w-[220px] transition-all ${selected ? 'border-orange-500 ring-2 ring-orange-500/30' : 'border-orange-400'}`}>
      {/* Toolbar flutuante */}
      <FlowNodeToolbar
        nodeId={id}
        nodeType={nodeData.type}
        onEdit={onEdit}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onTestFromHere={onTestFromHere}
        isVisible={selected}
      />
      
      {/* Handle de entrada no topo */}
      <Handle
        type="target"
        position={Position.Top}
        className="!bg-orange-500 !w-3 !h-3"
      />
      
      {/* Cabeçalho do node */}
      <div className="bg-orange-500/10 px-3 py-2 rounded-t-lg border-b border-orange-200 dark:border-orange-800 flex items-center gap-2">
        <div className="bg-orange-500/20 text-orange-600 p-1.5 rounded">
          <GitBranch className="h-4 w-4" />
        </div>
        <span className="font-semibold text-sm text-foreground">{nodeData.config?.nickname || 'Condição'}</span>
      </div>
      
      {/* Corpo com lista de IFs */}
      <div className="p-2 space-y-2">
        {/* Lista de IFs */}
        {comparisons.map((comparison: any, index: number) => {
          const handleId = `if_${index}`;
          const isConnected = connectedHandles.has(handleId);
          const isEmpty = !comparison.conditions || comparison.conditions.length === 0;
          
          return (
            <div key={comparison.id || index} className="relative">
              <div className={`text-xs px-2 py-1.5 rounded border transition-all ${
                isEmpty 
                  ? 'bg-muted/30 border-dashed border-muted-foreground/30 text-muted-foreground' 
                  : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
              }`}>
                <div className="flex items-center gap-1">
                  <span className="font-medium text-green-700 dark:text-green-400">IF #{index + 1}</span>
                  <span className="text-muted-foreground truncate max-w-[120px]">
                    {formatIfSummary(comparison)}
                  </span>
                </div>
              </div>
              
              {/* Handle de saída para este IF */}
              <Handle
                type="source"
                position={Position.Right}
                id={handleId}
                className={`!w-3 !h-3 transition-all ${
                  isConnected 
                    ? '!bg-green-500' 
                    : '!bg-red-500 animate-pulse'
                }`}
                style={{ top: '50%', transform: 'translateY(-50%)' }}
              />
            </div>
          );
        })}
        
        {/* Bloco ELSE (sempre presente) */}
        <div className="relative">
          <div className="text-xs px-2 py-1.5 rounded border bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700">
            <span className="font-medium text-gray-600 dark:text-gray-400">Else</span>
          </div>
          
          {/* Handle de saída para ELSE */}
          <Handle
            type="source"
            position={Position.Right}
            id="else"
            className={`!w-3 !h-3 transition-all ${
              connectedHandles.has('else') 
                ? '!bg-gray-500' 
                : '!bg-red-500 animate-pulse'
            }`}
            style={{ top: '50%', transform: 'translateY(-50%)' }}
          />
        </div>
        
        {/* Indicador de configuração vazia */}
        {comparisons.length === 0 && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground px-2">
            <Settings2 className="h-3 w-3" />
            <span>Clique para configurar</span>
          </div>
        )}
      </div>
    </div>
  );
}
